import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import en from "./locales/en.json";

export const SUPPORTED_LANGS = ["en", "hi", "mr"] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];

export async function ensureLocaleLoaded(lng: string) {
  const normalized = (lng || "en").slice(0, 2) as Lang;
  if (!SUPPORTED_LANGS.includes(normalized)) return;
  if (normalized === "en") return;
  if (i18n.hasResourceBundle(normalized, "translation")) return;

  if (normalized === "hi") {
    const hi = await import("./locales/hi.json");
    i18n.addResourceBundle("hi", "translation", hi.default, true, true);
    return;
  }

  if (normalized === "mr") {
    const mr = await import("./locales/mr.json");
    i18n.addResourceBundle("mr", "translation", mr.default, true, true);
  }
}

if (!i18n.isInitialized) {
  const originalChangeLanguage = i18n.changeLanguage.bind(i18n);
  i18n.changeLanguage = async (
    lng?: string,
    callback?: (t: (...args: any[]) => string) => void,
  ) => {
    if (lng) {
      await ensureLocaleLoaded(lng);
    }
    return originalChangeLanguage(lng, callback);
  };

  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: {
        en: { translation: en },
      },
      fallbackLng: "en",
      supportedLngs: SUPPORTED_LANGS as unknown as string[],
      load: "languageOnly",
      interpolation: { escapeValue: false },
      detection: {
        order: ["localStorage", "navigator", "htmlTag"],
        caches: ["localStorage"],
        lookupLocalStorage: "sonytyres.lang",
      },
      returnObjects: true,
    });

  const initialLanguage = (i18n.language || "en").slice(0, 2);
  if (initialLanguage !== "en") {
    void ensureLocaleLoaded(initialLanguage).then(() => i18n.changeLanguage(initialLanguage));
  }
}

if (typeof document !== "undefined") {
  const apply = (lng: string) => {
    document.documentElement.lang = lng;
  };
  apply(i18n.language || "en");
  i18n.on("languageChanged", apply);
}

export default i18n;
